import { createModel } from "@/code-editor";
import styles from "@/components/file-list.module.css";
import {
	deleteFile,
	fileExists,
	fillMissingFolderNames,
	getFolderName,
	getValidFilename,
	isImage,
	isRenamable,
	openFile,
	renameFilePrompt,
	renameFiles,
	save,
	selectLocalFile,
	visitPage,
} from "@/file";
import globals from "@/globals";
import * as signals from "@/signals";
import { isSiteOpened } from "@/site";
import { Column } from "@/types";
import {
	downloadText,
	handleKeyboardClick,
	showContextMenu,
	showDialog,
	showPrompt,
	showToast,
	sleep,
} from "@/utils";
import {
	faCircleCheck,
	faCirclePlus,
	faDownload,
	faFileExport,
	faFolderOpen,
	faGlobe,
	faListCheck,
	faPenToSquare,
	faPlus,
	faTrashCan,
	faUpload,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import { t } from "i18next";
import { Fragment, JSX, MouseEvent, useEffect, useState } from "react";

function FileList(): JSX.Element {
	const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);

	const completedFilenames = fillMissingFolderNames(
		Object.keys(signals.files.value),
	);
	const filenames = [];
	const folderNames = [];
	const sorted = completedFilenames.sort().filter((item) => {
		if (item.endsWith("/")) {
			item = item.slice(0, -1);
		}
		const folder = getFolderName(item);
		return signals.currentFolder.value === folder;
	});
	for (const name of sorted) {
		if (
			name === "settings.rth" ||
			name.endsWith(".js.rth") ||
			name.endsWith(".md.html") ||
			name.endsWith(".ts.rth")
		) {
			continue;
		}
		if (name.endsWith("/")) {
			folderNames.push(name);
		} else {
			filenames.push(name);
		}
	}
	const filteredFilenames = folderNames.concat(filenames);
	const isPlaceholderShown =
		filteredFilenames.length === 0 && !signals.currentSite.value;
	const isSelected = signals.currentColumn.value === Column.FILE_LIST;

	const pathParts = signals.currentFolder.value.split("/");
	pathParts.pop();

	const clearSelectedFiles = (): void => {
		signals.selectedFiles.value = [];
		setIsMultiSelectMode(false);
	};

	const clicked = (name: string, event?: MouseEvent): void => {
		event?.stopPropagation();
		if (signals.files.value[name] === undefined) {
			// for pesudo folders
			if (name.endsWith("/")) {
				signals.currentFolder.value = name;
			}
			return;
		}
		if (
			isMultiSelectMode ||
			(event && (event.ctrlKey || event.metaKey || event.shiftKey))
		) {
			selectFile(name, event);
			return;
		}
		if (
			globals.isMobile ||
			(typeof signals.files.value[name] !== "string" && !isImage(name))
		) {
			rightClicked(name, event);
			return;
		}
		signals.selectedFiles.value = [];
		void openFile(name, event);
		signals.currentFolder.value = getFolderName(name);
	};

	const getDisplayedFilename = (path: string): string => {
		if (path.endsWith("/")) {
			path = path.slice(0, -1);
		}
		if (path.includes("/")) {
			path = path.split("/").at(-1) || "";
		}
		if (!path) {
			return t("untitled");
		}
		return path;
	};

	const getParentFolderPath = (): string => {
		let path = pathParts.slice(0, -1).join("/");
		if (path) {
			path += "/";
		}
		return path;
	};

	const goToFolder = (endIndex: number): void => {
		if (endIndex === pathParts.length) {
			return;
		}
		let path = pathParts.slice(0, endIndex).join("/");
		if (path) {
			path += "/";
		}
		signals.currentFolder.value = path;
	};

	const goToParentFolder = (): void => {
		goToFolder(-1);
	};

	const moveFiles = async (): Promise<void> => {
		const folders = Object.keys(signals.files.value)
			.filter((file) => {
				return file.endsWith("/");
			})
			.map((folder) => {
				return folder.substring(0, folder.length - 1);
			});
		let newFolderName = await showPrompt(
			t("enterDestinationFolderNameToWhichMove"),
			{
				dataList: folders,
			},
		);
		newFolderName = getValidFilename(newFolderName, "/");
		const oldToNewNames = new Map<string, string>();
		const selectedFiles = [...signals.selectedFiles.value];

		// if the current file is a folder, it is not in the view; do not select it
		if (!signals.currentFile.value.endsWith("/")) {
			selectedFiles.push(signals.currentFile.value);
		}

		for (const oldName of selectedFiles) {
			if (oldName.endsWith("/")) {
				const newFolder =
					newFolderName + oldName.split("/").at(-2) + "/";
				oldToNewNames.set(oldName, newFolder);
			} else {
				const newFileName = newFolderName + oldName.split("/").at(-1);
				oldToNewNames.set(oldName, newFileName);
			}
		}

		// check if new names already exist
		for (const newName of oldToNewNames.values()) {
			if (fileExists(newName)) {
				return;
			}
		}

		const newNamesPreview = Array.from(oldToNewNames.entries()).map(
			([oldName, newName]) => {
				return `${oldName} -> ${newName}`;
			},
		);
		await showDialog(
			t("confirmMoveFollowingFiles") + "\n" + newNamesPreview.join("\n"),
			{
				showCancel: true,
			},
		);
		await sleep(globals.ANIMATION_WAIT_TIME);
		renameFiles(oldToNewNames);
	};

	const newFile = (event?: MouseEvent): void => {
		if (!isSiteOpened()) {
			return;
		}
		showContextMenu(event, [
			{
				content: {
					icon: faCirclePlus,
					text: t("file"),
				},
				onClick: async (): Promise<void> => {
					await sleep(globals.ANIMATION_WAIT_TIME);
					let filename = await showPrompt(t("enterFilename"));
					if (!filename) {
						return;
					}
					if (signals.currentFile.value.includes("/")) {
						filename = signals.currentFolder.value + filename;
					}
					filename = getValidFilename(filename);
					if (!fileExists(filename)) {
						signals.files.value = {
							...signals.files.value,
							[filename]: "",
						};
						createModel(filename);
						void save({
							key: filename,
							type: "string",
						});
						void openFile(filename);
					}
				},
				when: true,
			},
			{
				content: {
					icon: faCirclePlus,
					text: t("folder"),
				},
				onClick: async (): Promise<void> => {
					await sleep(globals.ANIMATION_WAIT_TIME);
					let name = await showPrompt(t("enterFolderName"));
					if (!name) {
						return;
					}
					if (signals.currentFile.value.includes("/")) {
						name = signals.currentFolder.value + name;
					}
					name = getValidFilename(name, "/");
					if (!fileExists(name)) {
						signals.files.value = {
							...signals.files.value,
							[name]: "",
						};
						void save({
							key: name,
						});
						void openFile(name);
						signals.currentFolder.value = name;
					}
				},
				when: true,
			},
			{
				content: {
					icon: faUpload,
					text: t("uploadFile"),
				},
				onClick: (): void => {
					selectLocalFile({
						accept: "",
						multiple: true,
					});
				},
				when: true,
			},
			{
				content: {
					icon: faUpload,
					text: t("uploadFolder"),
				},
				onClick: async (): Promise<void> => {
					await sleep(globals.ANIMATION_WAIT_TIME);
					void showDialog(t("dragFolderToUpload"));
				},
				when: !globals.isMobile,
			},
		]);
	};

	const rightClicked = (name: string, event?: MouseEvent): void => {
		event?.preventDefault();
		showContextMenu(event, [
			{
				content: {
					icon: faCircleCheck,
					text: signals.selectedFiles.value.includes(name)
						? t("unselect")
						: t("select"),
				},
				onClick: (): void => {
					selectFile(name, event);
				},
				when:
					globals.isMobile &&
					isMultiSelectMode &&
					name !== signals.currentFile.value,
			},
			{
				content: {
					icon: faFolderOpen,
					text: t("open"),
				},
				onClick: (): void => {
					void openFile(name, event);
					signals.currentFolder.value = getFolderName(name);
				},
				when:
					globals.isMobile &&
					signals.selectedFiles.value.length === 0 &&
					(typeof signals.files.value[name] === "string" ||
						isImage(name)),
			},
			{
				content: {
					icon: faGlobe,
					text: t("visit"),
				},
				onClick: (): void => {
					visitPage(name);
				},
				when: signals.selectedFiles.value.length === 0,
			},
			{
				content: {
					icon: faPenToSquare,
					text: t("rename"),
				},
				onClick: async (): Promise<void> => {
					await sleep(globals.ANIMATION_WAIT_TIME);
					void renameFilePrompt(name);
				},
				when: isRenamable(name),
			},
			{
				content: {
					icon: faFileExport,
					text: t("move"),
				},
				onClick: (): void => {
					void moveFiles();
				},
				when: signals.selectedFiles.value.length > 0,
			},
			{
				content: {
					icon: faDownload,
					text: t("download"),
				},
				onClick: (): void => {
					const fileContent = signals.files.value[name];
					if (typeof fileContent === "string") {
						downloadText(fileContent, name);
					}
				},
				when:
					signals.selectedFiles.value.length === 0 &&
					typeof signals.files.value[name] === "string",
			},
			{
				content: {
					icon: faTrashCan,
					isDangerous: true,
					text: t("delete"),
				},
				onClick: async (): Promise<void> => {
					await sleep(globals.ANIMATION_WAIT_TIME);
					void deleteFile(name);
				},
				when: true,
			},
			{
				content: {
					icon: faListCheck,
					text: isMultiSelectMode
						? t("exitMultiSelect")
						: t("multiSelect"),
				},
				onClick: (): void => {
					const newValue = !isMultiSelectMode;
					setIsMultiSelectMode(newValue);
					if (newValue) {
						if (name !== signals.currentFile.value) {
							selectFile(name, event);
						}
					} else {
						clearSelectedFiles();
					}
				},
				when: true,
			},
		]);
	};

	const selectFile = (name: string, event?: MouseEvent): void => {
		if (name === signals.currentFile.value) {
			clearSelectedFiles();
			return;
		}
		let newSelected: string[];
		if (event?.shiftKey) {
			newSelected = [];
			const currentIndex = filteredFilenames.indexOf(
				signals.currentFile.value,
			);
			const targetIndex = filteredFilenames.indexOf(name);
			let max, min;
			if (targetIndex > currentIndex) {
				max = targetIndex;
				min = currentIndex + 1;
			} else {
				max = currentIndex - 1;
				min = targetIndex;
			}
			for (let i = min; i <= max; i++) {
				newSelected.push(filteredFilenames[i]);
			}
			setIsMultiSelectMode(true);
		} else {
			newSelected = [...signals.selectedFiles.value];
			if (newSelected.includes(name)) {
				newSelected.splice(newSelected.indexOf(name), 1);
			} else {
				newSelected.push(name);
				setIsMultiSelectMode(true);
			}
		}
		signals.selectedFiles.value = newSelected;
		void showToast(
			t("filesSelected", {
				count: newSelected.length + 1,
			}),
		);
	};

	const breadcrumbElements = pathParts.map((part, index) => {
		const handleClick = (): void => {
			goToFolder(index + 1);
		};

		return (
			<Fragment key={index}>
				<span
					className="breadcrumb-item"
					onClick={handleClick}
					onKeyDown={handleKeyboardClick(handleClick)}
					role="button"
					tabIndex={0}
				>
					{getDisplayedFilename(part)}
				</span>
				{" / "}
			</Fragment>
		);
	});

	const listElements = filteredFilenames.map((name) => {
		const handleClick = (event?: MouseEvent): void => {
			clicked(name, event);
		};

		const handleContextMenu = (event: MouseEvent): void => {
			rightClicked(name, event);
		};

		const handleDragStart = (): void => {
			globals.dragged = name;
		};

		return (
			<li
				aria-selected={
					signals.currentFile.value === name ||
					signals.selectedFiles.value.includes(name)
				}
				className={clsx(
					signals.changedFiles.value.includes(name) && "changed",
					name.endsWith("/") && "folder",
					isMultiSelectMode && styles["multi-select"],
				)}
				data-path={name}
				draggable
				key={name}
				onClick={handleClick}
				onContextMenu={handleContextMenu}
				onDragStart={handleDragStart}
				onKeyDown={handleKeyboardClick(handleClick)}
				role="option"
				tabIndex={0}
			>
				{isMultiSelectMode && (
					<input
						aria-label={getDisplayedFilename(name)}
						checked={
							signals.currentFile.value === name ||
							signals.selectedFiles.value.includes(name)
						}
						onClick={handleClick}
						type="checkbox"
					/>
				)}
				{getDisplayedFilename(name)}
			</li>
		);
	});

	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent): void => {
			if (event.key === "Escape") {
				clearSelectedFiles();
			}
		};
		window.addEventListener("keydown", handleKeyDown);
		return (): void => {
			window.removeEventListener("keydown", handleKeyDown);
		};
	}, []);

	useEffect(() => {
		return signals.currentFile.subscribe(() => {
			setIsMultiSelectMode(false);
		});
	}, []);

	return (
		<div
			aria-hidden={!isSelected}
			aria-label={t("clearSelection")}
			className={clsx(
				"list",
				styles["file-list"],
				isSelected && "selected",
			)}
			onClick={clearSelectedFiles}
			onKeyDown={handleKeyboardClick(clearSelectedFiles)}
			role="button"
			tabIndex={0}
		>
			<div
				aria-haspopup={signals.currentSite.value ? "menu" : "dialog"}
				className="head-button"
				onClick={newFile}
				onKeyDown={handleKeyboardClick(newFile)}
				role="button"
				tabIndex={0}
			>
				<FontAwesomeIcon icon={faPlus} />
				<span>{t("newFile")}</span>
			</div>
			{!isPlaceholderShown && (
				<ul>
					<>
						{signals.currentFolder.value !== "" && (
							<>
								<li
									className={clsx(
										styles["breadcrumb"],
										"breadcrumb",
									)}
								>
									{breadcrumbElements}
								</li>
								<li
									className="folder"
									data-path={getParentFolderPath()}
									onClick={goToParentFolder}
									onKeyDown={handleKeyboardClick(
										goToParentFolder,
									)}
									role="menuitem"
									tabIndex={0}
								>
									{"../" + t("backToParentFolder")}
								</li>
							</>
						)}
						{listElements}
					</>
				</ul>
			)}
			{isPlaceholderShown && signals.currentSite.value && (
				<div className="list-placeholder">{t("noFiles")}</div>
			)}
		</div>
	);
}

export default FileList;
